import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import * as Yup from "yup";
import SelectFiled from "../../common/textfield/SelectFiled";
import Select from 'react-select';
import { useSelector } from "react-redux";
import TextArea from "../../common/textfield/TextArea";
import { Button } from "../../common/Button";
import { getPractitionerList, getPractitionerAuthorizedList } from "../../api/Individual";
import TaskPatientSelect from "./TaskPatientSelect";
import { createTask } from "../../api/Task";
import { failed, success } from "../../common/Toastify";
import { he } from "date-fns/locale";
import { capitalizeWords } from "../../common/functions";
import { validation, conditionalValidation } from "../../../utils/constants";

export const PRIORITY_LEVELS = [1, 2, 3, 4, 5];

const TASK_TYPES = [
  { name: "Admin Task", value: "Admin Task" },
  { name: "Review Lab Results", value: "Review Lab Results" },
  { name: "Schedule an Appointment", value: "Schedule an Appointment" },
];

const PRIORITY_OPTIONS = PRIORITY_LEVELS.map((p) => ({
  name: p,
  value: p,
}));

export default function CreateTaskModal(props) {
  const [practitionerOptions, setPractitionerOptions] = useState([]);
  const [showPatientSelect, setShowPatientSelect] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const organizations =  props.organizations?.map((org) => ({ name: org?.label, value: org?.value }));
  const [orgId, setOrgId] = useState(props.orgId ?? null);
  const onHide = props.onHide;
  const onTaskCreated = props.onTaskCreated;
  let show = props.show;
  
  const practitionerId = useSelector(
    (state) => state?.auth?.user?.["custom:practitioner_id"]
  );
  const practUserName = useSelector(
    (state) => state?.auth?.user?.name[0]?.text
  );

  const formik = useFormik({
    initialValues: {
      type: "",
      priority: "",
      description: "",
      practitioner: "",
      patient: null,
      organization: orgId,
    },
    validationSchema: Yup.object({
      type: conditionalValidation(
        Yup.string(),
        {
          validation,
          label: "Type",
          mandatory: true
        }),
      priority: conditionalValidation(
        Yup.string(),
        {
          validation,
          label: "Priority",
          mandatory: true
        }),
      description: conditionalValidation(
          Yup.string().max(500, "Too long"),
          {
            validation,
            label: "Description",
            mandatory: true
          })
      // practitioner is optional
      // patient is optional
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const selectedPractitioner = practitionerOptions.find(
        (p) => p.value === values.practitioner
        );

      const payload = {
        resourceType: "Task",
        status: "new", // Always new on create
        code: { text: TASK_TYPES.find(t => t.value === values.type)?.name || "" },
        priority: Number(values.priority),
        description: values.description,
        orgId: values.organization,
        owner: values.practitioner ? {
                  reference: `Practitioner/${values.practitioner}`,
                  display: selectedPractitioner?.label || "",
              }
              : undefined,
        for: values.patient
              ? {
                  reference: `Patient/${values.patient.id}`,
                  display: values.patient.display,
                }
              : undefined
      };
      try {
        await createTask(payload);
        success("Task created successfully");
        onTaskCreated?.();
        resetForm();
        onHide();
      } catch (err) {
        failed(err?.response?.data?.message || "Failed to create task");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleOrgChange = (input) => {
    const value =
      input?.target?.value ??
      input?.value ??
      input ??
      null;
      formik.setFieldValue("organization", value);
      setOrgId(value);
      formik.setFieldValue("practitioner", "")
      setPractitionerOptions([]);
      setSelectedPatient(null);
      formik.setFieldValue("patient", null);
    };
    
   useEffect(() => {
    if (props.orgId) setOrgId(props.orgId);
  }, [props.orgId]);

  const userDefaultOrgId = useSelector(
    (state) => state?.auth?.user?.["organizations"]?.[0]?.id
  );

  useEffect(() => {
    if (!orgId && userDefaultOrgId) setOrgId(userDefaultOrgId);
  }, [orgId, userDefaultOrgId]);

  useEffect(() => {
    // if (!orgId) return;
    getPractitionerAuthorizedList(orgId)
      .then(res => {
        if (res.status) {
          let unsorted = res.result.filter(item => item.name || false);
          let practitionerOptionsToDisplay = unsorted
            .map(practitioner => ({
              value: practitioner.id,
              label: capitalizeWords(practitioner.name?.[0]?.text),
              data: practitioner,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setPractitionerOptions(practitionerOptionsToDisplay);
          formik.setFieldValue("practitioner", "");
        }
      })
      .catch(() => {});
  }, [orgId]);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Task</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit} className="common-form border-fields">
        <Modal.Body>
          <Row>
            <Col>
              <SelectFiled
                keyField="type"
                label="Type"
                formik={formik}
                options={TASK_TYPES}
                placeholder="Select Type"
              />
            </Col>
            <Col>
              <SelectFiled
                keyField="priority"
                label="Priority"
                formik={formik}
                options={PRIORITY_OPTIONS}
                placeholder="Select Priority"
              />
            </Col>
          </Row>
          {organizations?.length > 1 &&  <Row><Col>
          <SelectFiled
            keyField={"organization"}
            label={"Select Organization"}
            selectLabel="Select"
            formik={formik}
            options={organizations}
            isLoading={organizations?.length > 1 ? false : true}
            readOnly={organizations?.length > 1 ? false : true}
            hideRequired={true}
            onChange={handleOrgChange}
          />
        </Col></Row>}
          <Row>
            <Col>
              <TextArea
                keyField="description"
                label="Description"
                formik={formik}
                placeholder="Task Description"
                maxLength={500}
              />
            </Col>
          </Row>
          <Row>
            <Col>
            <div className="field-wrap">
                <label className="searchSelectLabel">Assign Practitioner (Optional)</label>
                <Select
                    onChange={option => {
                    formik.setFieldValue('practitioner', option ? option.value : "");
                    }}
                    value={practitionerOptions.find(o => o.value === formik.values.practitioner) || null}
                    name="practitioner"
                    options={practitionerOptions}
                    className="search-select"
                    classNamePrefix="select"
                    isClearable={true}
                    placeholder="Select Practitioner"
                />
                <div>
                    {formik.touched.practitioner && formik.errors.practitioner
                    ? <div className="error-text">{formik.errors.practitioner}</div>
                    : null}
                </div>
                </div>

            </Col>
            <Col>
               <div className="field-wrap">
                <label htmlFor="patient" className="form-label">
                  Assign Patient (Optional)
                </label>
                <Button
                    // width="100%"
                    className="w-100"
                    type="button"
                    variant="secondary"
                    onClick={() => setShowPatientSelect(true)}
                >
                    {selectedPatient
                    ? `${selectedPatient.display}`
                    : "Select Patient"}
                </Button>
              </div>
            </Col>
          </Row>
          {/* Show error for patient if needed */}
          {formik.touched.patient && formik.errors.patient ? (
            <div className="error-text">{formik.errors.patient}</div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={formik.isSubmitting}
          >
            Create
          </Button>
        </Modal.Footer>
      </form>
      {/* Patient select modal */}
      <TaskPatientSelect
        show={showPatientSelect}
        orgId={orgId}
        onHide={() => setShowPatientSelect(false)}
        onPatientSelected={(patient) => {
          setShowPatientSelect(false);
          setSelectedPatient({
            id: patient.id,
            display:
              patient?.name?.[0]?.text?.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ') || "(Unnamed)",
          });
          formik.setFieldValue("patient", {
            id: patient.id,
            display:
              patient?.name?.[0]?.text?.split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ') || "(Unnamed)",
          });
        }}
      />
    </Modal>
  );
}
